import { Injectable, signal, computed } from '@angular/core';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
  Auth,
  sendPasswordResetEmail,
  confirmPasswordReset,
  verifyPasswordResetCode
} from 'firebase/auth';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly app = initializeApp(environment.firebase);
  private readonly auth: Auth = getAuth(this.app);

  // Expose current user state using an Angular signal
  private readonly _currentUser = signal<User | null>(null);
  public readonly currentUser = this._currentUser.asReadonly();

  // Expose authenticated state as computed signal
  public readonly isAuthenticated = computed(() => this._currentUser() !== null);

  // Track initialization status of Firebase Auth
  private readonly authInitialized$ = new BehaviorSubject<boolean>(false);

  constructor() {
    // Listen to authentication state changes
    onAuthStateChanged(this.auth, (user) => {
      this._currentUser.set(user);
      if (!this.authInitialized$.value) {
        this.authInitialized$.next(true);
      }
    });

    // Automatically intercept and authorize all outgoing backend fetch requests
    this.setupFetchInterceptor();
  }

  /**
   * Helper to wait until Firebase Auth is fully initialized
   */
  async waitForInit(): Promise<void> {
    if (this.authInitialized$.value) {
      return;
    }
    return new Promise<void>((resolve) => {
      const sub = this.authInitialized$.subscribe((init) => {
        if (init) {
          sub.unsubscribe();
          resolve();
        }
      });
    });
  }

  /**
   * Intercept global fetch to add Authorization: Bearer <ID Token>
   * for any request targeting our backend API.
   */
  private setupFetchInterceptor(): void {
    const originalFetch = globalThis.fetch;

    globalThis.fetch = async (input, init) => {
      const user = this.auth.currentUser;

      let requestUrl = '';
      if (typeof input === 'string') {
        requestUrl = input;
      } else if (input instanceof Request) {
        requestUrl = input.url;
      } else {
        requestUrl = input.toString();
      }

      // Only attach bearer token to internal backend calls
      if (user && requestUrl.startsWith(environment.backend.baseUrl)) {
        try {
          const token = await user.getIdToken();
          init = init || {};
          const headers = new Headers(init.headers || {});
          headers.set('Authorization', `Bearer ${token}`);
          init.headers = headers;
        } catch (error) {
          console.error('Failed to attach auth token to fetch request:', error);
        }
      }
      return originalFetch(input, init);
    };
  }

  /**
   * Sign in using email and password
   * @param email The user's email address
   * @param password The user's password
   */
  async signIn(email: string, password: string): Promise<User> {
    try {
      const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
      return userCredential.user;
    } catch (error) {
      console.error('Firebase sign-in error:', error);
      throw error;
    }
  }

  /**
   * Sign out the currently authenticated user
   */
  async logout(): Promise<void> {
    try {
      await signOut(this.auth);
    } catch (error) {
      console.error('Firebase sign-out error:', error);
      throw error;
    }
  }

  /**
   * Send a password reset email
   * @param email The user's email address
   */
  async sendPasswordResetEmail(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(this.auth, email);
    } catch (error) {
      console.error('Firebase password reset email error:', error);
      throw error;
    }
  }

  /**
   * Confirm password reset
   * @param code The action code sent in the reset email (oobCode)
   * @param newPassword The user's new password
   */
  async confirmPasswordReset(code: string, newPassword: string): Promise<void> {
    try {
      await confirmPasswordReset(this.auth, code, newPassword);
    } catch (error) {
      console.error('Firebase confirm password reset error:', error);
      throw error;
    }
  }

  /**
   * Verify password reset action code and retrieve the user's email
   * @param code The action code sent in the reset email (oobCode)
   */
  async verifyPasswordResetCode(code: string): Promise<string> {
    try {
      return await verifyPasswordResetCode(this.auth, code);
    } catch (error) {
      console.error('Firebase verify password reset code error:', error);
      throw error;
    }
  }
}
